/**
 * 텍스트 입력 + 드롭다운 자동완성 (콤보박스).
 *
 * 동작:
 *   - 항상 자유 타이핑 가능 — 입력값이 곧 form 의 값.
 *   - 포커스 / 입력 시 드롭다운 표시:
 *       1. 맨 위: ✏ 「직접 입력 — '<현재 입력>'」 (입력값 그대로 확정)
 *       2. 그 아래: 기존 목록 중 입력어 매칭 (시작-일치 우선 + 부분일치)
 *   - 클릭 / Enter 로 항목 선택, ↑↓ 로 이동, Esc 닫기.
 *   - 클릭 외부 시 자동 닫기.
 *
 * 사용:
 *   <ComboboxInput
 *     label="엔드유저"
 *     value={form.endUser}
 *     onChange={(v) => set('endUser', v)}
 *     suggestions={partiesQuery.data?.endUsers ?? []}
 *     placeholder="강남구청…"
 *   />
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

export interface ComboboxInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  /** 1 = 한 칼럼, 2 = 두 칼럼 너비 (form grid 안에서). 미지정 시 그리드 자체에 맡김. */
  colSpan?: 1 | 2;
  /**
   * 'key' = 🟡 주요 항목 칸 — 흐린 노란 바탕 (딜등록 입력창 · v2.193.1).
   * 미지정이면 종전 그대로(흰 바탕) — 다른 화면의 모양은 건드리지 않는다.
   */
  accent?: 'key';
  /** 최대 표시 항목 (기본 12 + 신규 등록 1) */
  maxItems?: number;
}

function filterMatched(candidates: string[], q: string, limit: number): string[] {
  if (candidates.length === 0) return [];
  const lower = q.toLowerCase();
  if (q === '') return candidates.slice(0, limit);
  const starts: string[] = [];
  const contains: string[] = [];
  for (const c of candidates) {
    if (!c) continue;
    const lc = c.toLowerCase();
    if (lc === lower) continue;
    if (lc.startsWith(lower)) starts.push(c);
    else if (lc.includes(lower)) contains.push(c);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

export function ComboboxInput({
  label,
  value,
  onChange,
  suggestions,
  placeholder,
  colSpan,
  accent,
  maxItems = 12,
}: ComboboxInputProps) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1); // -1 = 신규 등록 행
  const wrapRef = useRef<HTMLDivElement>(null);

  const matched = useMemo<string[]>(
    () => filterMatched(suggestions, value.trim(), maxItems),
    [suggestions, value, maxItems],
  );

  // 바깥 클릭 → 닫기
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function pick(s: string) {
    onChange(s);
    setOpen(false);
    setActiveIdx(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      if (open && activeIdx >= 0 && activeIdx < matched.length) {
        e.preventDefault();
        pick(matched[activeIdx]!);
      } else if (open) {
        // -1 = 직접 입력 행 — 그냥 드롭다운 닫기 (값은 이미 input 에 있음)
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIdx(-1);
      }
      return;
    }
    if (!open) return;
    const total = matched.length; // -1 ~ matched.length-1
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1 > total - 1 ? -1 : i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 < -1 ? total - 1 : i - 1));
    }
  }

  const span = colSpan === 2 ? 'col-span-2' : '';

  return (
    <div ref={wrapRef} className={`relative ${span}`}>
      <label className="ui-label">{label}</label>
      <input
        type="text"
        className={`w-full border rounded px-2 py-1.5 mt-1 text-sm outline-none focus:border-blue-500 pr-7 ${
          accent === 'key' ? 'border-amber-300 bg-amber-50/70' : 'border-slate-300'
        }`}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => {
          // mousedown 으로 blur 이전 처리 — 포커스 안 잃고 토글
          e.preventDefault();
          setOpen((v) => !v);
        }}
        aria-label="목록 열기/닫기"
        className="absolute right-1.5 top-[1.85rem] text-slate-500 hover:text-slate-700 text-xs px-1"
      >
        ▾
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded shadow-lg max-h-72 overflow-y-auto text-sm"
        >
          {/* 신규 등록 행 — 항상 맨 위 */}
          <li
            role="option"
            aria-selected={activeIdx === -1}
            onMouseDown={(e) => {
              e.preventDefault();
              // 직접 입력 — 그냥 드롭다운 닫기 (입력값 유지)
              setOpen(false);
              setActiveIdx(-1);
            }}
            onMouseEnter={() => setActiveIdx(-1)}
            className={`px-3 py-1.5 cursor-pointer border-b border-slate-100 flex items-center justify-between ${
              activeIdx === -1
                ? 'bg-blue-50 text-blue-700'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className="font-medium">✏ 목록에 없음 (직접 입력)</span>
            <span className="text-xs text-slate-500">
              {value.trim() === '' ? '' : `「${value.trim()}」`}
            </span>
          </li>

          {/* 기존 목록 매칭 */}
          {matched.length === 0 ? (
            <li className="px-3 py-3 text-xs text-slate-500 text-center">
              {suggestions.length === 0
                ? '기존 입력 자료 없음 — 신규 등록만 가능'
                : '매칭되는 기존 자료 없음'}
            </li>
          ) : (
            matched.map((s, i) => {
              const active = i === activeIdx;
              return (
                <li
                  key={`${s}-${i}`}
                  role="option"
                  aria-selected={active}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(s);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`px-3 py-1.5 cursor-pointer ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {highlight(s, value.trim(), active)}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

function highlight(text: string, q: string, active: boolean) {
  if (q === '') return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span
        className={`font-semibold ${active ? 'underline' : 'text-blue-600'}`}
      >
        {text.slice(idx, idx + q.length)}
      </span>
      {text.slice(idx + q.length)}
    </>
  );
}
